import React, { useEffect, useState, createContext, useContext } from 'react';
import { subscribeToAuth, getCustomerProfile, signIn as fbSignIn, signUp as fbSignUp, signOutUser, sendPasswordReset, resendVerificationEmail, updateCustomerProfile, type CustomerProfile, type User } from '../utils/firebase';
interface AuthContextValue {
  user: User | null;
  profile: CustomerProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, marketingOptIn?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (data: Partial<CustomerProfile>) => Promise<void>;
}
const AuthContext = createContext<AuthContextValue | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  // Subscribe to auth state changes
  useEffect(() => {
    const unsub = subscribeToAuth(async fbUser => {
      setUser(fbUser);
      if (fbUser) {
        try {
          const p = await getCustomerProfile(fbUser.uid);
          setProfile(p);
        } catch (err) {
          console.error('[Auth] Failed to load profile:', err);
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);
  const refreshProfile = async () => {
    if (!user) return;
    const p = await getCustomerProfile(user.uid);
    setProfile(p);
  };
  const value: AuthContextValue = {
    user,
    profile,
    loading,
    signIn: async (email, password) => {
      await fbSignIn(email, password);
    },
    signUp: async (email, password, displayName, marketingOptIn) => {
      await fbSignUp(email, password, displayName, marketingOptIn);
    },
    signOut: async () => {
      await signOutUser();
    },
    resetPassword: async email => {
      await sendPasswordReset(email);
    },
    resendVerification: async () => {
      await resendVerificationEmail();
    },
    refreshProfile,
    updateProfile: async data => {
      if (!user) throw new Error('Not signed in');
      await updateCustomerProfile(user.uid, data);
      await refreshProfile();
    }
  };
  return <AuthContext.Provider value={value} data-id="element-2851">{children}</AuthContext.Provider>;
}
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}